import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { 
  fetchUserRegistrationStats, 
  fetchTotalViews,
  fetchAdStatistics,
  fetchAdsTopStats,
  fetchTopAgenciesRanking
} from '../../services/kpi';
import { 
  UserRegistrationStatsData, 
  TotalViewsData, 
  AdStatisticsData,
  AdsTopStatsData,
  TopAgenciesRankingData
} from '../../services/kpi';

interface KpiSummaryProps {
  startDate?: string;
  endDate?: string;
}

const TopStatsList: React.FC<{
  title: string;
  items: { id: string; name: string; count: number }[];
  totalPublishEvents?: number;
  emptyMessage: string;
}> = ({ title, items, totalPublishEvents, emptyMessage }) => {
  const listTotal = items.reduce((sum, item) => sum + item.count, 0);

  return (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {totalPublishEvents != null && (
        <Typography variant="caption" color="text.secondary" display="block">
          Total publish events: {totalPublishEvents}
        </Typography>
      )}
      <Divider sx={{ my: 1 }} />
      {items.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          {items.map((item, index) => (
            <Box
              key={`${item.id}-${index}`}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 0.75,
                borderBottom: index < items.length - 1 ? '1px solid #f0f0f0' : 'none',
              }}
            >
              <Typography
                variant="body2"
                sx={item.id === 'others' ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}
              >
                {item.id === 'others' ? item.name : `${index + 1}. ${item.name}`}
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {item.count}
              </Typography>
            </Box>
          ))}
          {totalPublishEvents != null && listTotal !== totalPublishEvents && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              List total ({listTotal}) does not match period total ({totalPublishEvents})
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {emptyMessage}
        </Typography>
      )}
    </CardContent>
  </Card>
  );
};

const KpiSummary: React.FC<KpiSummaryProps> = ({ startDate, endDate }) => {
  const [adStats, setAdStats] = useState<AdStatisticsData | null>(null);
  const [userStats, setUserStats] = useState<UserRegistrationStatsData | null>(null);
  const [totalViews, setTotalViews] = useState<TotalViewsData | null>(null);
  const [adsTopStats, setAdsTopStats] = useState<AdsTopStatsData | null>(null);
  const [topAgencies, setTopAgencies] = useState<TopAgenciesRankingData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [adStatsData, userStatsData, totalViewsData, adsTopStatsData, topAgenciesData] = await Promise.all([
          fetchAdStatistics({ startDate, endDate }),
          fetchUserRegistrationStats({ startDate, endDate }),
          fetchTotalViews({ startDate, endDate }),
          fetchAdsTopStats({ startDate, endDate }),
          fetchTopAgenciesRanking({ startDate, endDate }),
        ]);

        setAdStats(adStatsData);
        setUserStats(userStatsData);
        setTotalViews(totalViewsData);
        setAdsTopStats(adsTopStatsData);
        setTopAgencies(topAgenciesData);
      } catch (error) {
        console.error('Error fetching KPI summary:', error);
        setError('Failed to load KPI summary data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={4} sx={{ mb: 3, px: 1 }}>
      {/* Ad Publishing Stats */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Ad Publishing Statistics
            </Typography>
            
            <Divider sx={{ my: 1 }} />
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Published
                </Typography>
                <Typography variant="h5">
                  {adStats?.totalPublishedAds || 0}
                </Typography>
              </Grid>
              
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Average/Month
                </Typography>
                <Typography variant="h5">
                  {adStats?.averagePerMonth?.toFixed(1) || '0.0'}
                </Typography>
              </Grid>
              
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Published %
                </Typography>
                <Typography variant="h5">
                  {adStats?.monthPercentage?.toFixed(1) || '0.0'}%
                </Typography>
              </Grid>
              
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Views
                </Typography>
                <Typography variant="h5">
                  {totalViews?.totalViews?.toLocaleString() || 0}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      
      {/* User Registration Stats */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Registration Statistics
            </Typography>
            
            <Divider sx={{ my: 1 }} />
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Registrations
                </Typography>
                <Typography variant="h5">
                  {userStats?.total || 0}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Individual Users
                </Typography>
                <Typography variant="h5">
                  {userStats?.individualUsers || 0}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company Users
                </Typography>
                <Typography variant="h5">
                  {userStats?.companyUsers || 0}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Broker Users
                </Typography>
                <Typography variant="h5">
                  {userStats?.brokerUsers || 0}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Top Locations from Ads */}
      <Grid item xs={12} md={6}>
        <TopStatsList
          title="Top Locations (Ads)"
          items={adsTopStats?.topLocations || []}
          totalPublishEvents={adsTopStats?.totalPublishEvents}
          emptyMessage="No published ads with location data in this period"
        />
      </Grid>

      {/* Top Property Types from Ads */}
      <Grid item xs={12} md={6}>
        <TopStatsList
          title="Top Property Types (Ads)"
          items={adsTopStats?.topPropertyTypes || []}
          totalPublishEvents={adsTopStats?.totalPublishEvents}
          emptyMessage="No published ads with property type data in this period"
        />
      </Grid>

      {/* Top Agencies Ranking */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top Agencies Ranking
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Ranked by ads published, then ad views, then contact clicks (includes company + agent ads)
            </Typography>
            <Divider sx={{ my: 1 }} />
            {topAgencies?.agencies && topAgencies.agencies.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>Agency (Company)</TableCell>
                      <TableCell align="right">Ads Published</TableCell>
                      <TableCell align="right">Ad Views</TableCell>
                      <TableCell align="right">Contact Clicks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topAgencies.agencies.map((agency) => (
                      <TableRow key={agency.companyId} hover>
                        <TableCell>{agency.rank}</TableCell>
                        <TableCell>{agency.companyName}</TableCell>
                        <TableCell align="right">{agency.adsPublished}</TableCell>
                        <TableCell align="right">{agency.totalViews.toLocaleString()}</TableCell>
                        <TableCell align="right">{agency.totalContactClicks.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No agency activity in this period
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default KpiSummary;
